/**
 * The locales the public site serves (master D-11): `/{cc}/{lang}` with a
 * lowercase ISO 3166-1 country and an ISO 639-1 language.
 *
 * Pure module (no `window`, no React). The list must match the backend's
 * launched locales (`src/constants/locales.ts` there, and later its `locales`
 * query); launching a country is one release that changes both (plan W5.7).
 *
 * Locale routes are generated from this list with LITERAL segments
 * (`/pk/en/...`), never a dynamic `:cc/:lang` pattern, which would swallow
 * two-segment global URLs such as `/mobile/privacy-policy`. The locale always
 * comes from the URL; nothing here reads the browser language or the IP.
 */

export interface Locale {
  /** URL country segment, lowercase ISO 3166-1 alpha-2 (`pk`). */
  readonly cc: string;
  /** URL language segment, ISO 639-1 (`en`). */
  readonly lang: string;
  /** `hreflang` and `<html lang>` value (`en-PK`). */
  readonly hreflang: string;
  /** Country name used in titles ("in {Country}"), never hard-coded elsewhere. */
  readonly country: string;
  /** ISO 4217 currency for prices and structured data. */
  readonly currency: string;
  /** IANA time zone for every date shown or compared on the page. */
  readonly timeZone: string;
}

const PK_EN: Locale = Object.freeze({
  cc: 'pk',
  lang: 'en',
  hreflang: 'en-PK',
  country: 'Pakistan',
  currency: 'PKR',
  timeZone: 'Asia/Karachi',
});

const BH_EN: Locale = Object.freeze({
  cc: 'bh',
  lang: 'en',
  hreflang: 'en-BH',
  country: 'Bahrain',
  currency: 'BHD',
  timeZone: 'Asia/Bahrain',
});

/** Launched locales, default first. */
export const LOCALES: readonly Locale[] = Object.freeze([PK_EN, BH_EN]);

/** The locale every un-prefixed page belongs to while Pakistan is the only market. */
export const DEFAULT_LOCALE: Locale = PK_EN;

/**
 * The launched locale for a URL's country and language segments, or `null`
 * when that pair is not launched (the page is then a 404).
 *
 * Matching ignores case so the caller can 301 `/PK/EN/...` to its lowercase
 * canonical instead of 404ing it; route tables still use the lowercase
 * literals.
 */
export function parseLocale(
  cc: string | null | undefined,
  lang: string | null | undefined,
): Locale | null {
  if (!cc || !lang) return null;
  const c = cc.trim().toLowerCase();
  const l = lang.trim().toLowerCase();
  return LOCALES.find((locale) => locale.cc === c && locale.lang === l) ?? null;
}

/** Upper-case ISO country code (`PK`), as the backend's `countries.code` stores it. */
export function countryCode(locale: Locale = DEFAULT_LOCALE): string {
  return locale.cc.toUpperCase();
}

/** Open Graph locale (`en_PK`). */
export function ogLocale(locale: Locale = DEFAULT_LOCALE): string {
  return `${locale.lang}_${locale.cc.toUpperCase()}`;
}

/** The `pk-en` form the backend resolver takes as `?locale=`. */
export function localeKey(locale: Locale = DEFAULT_LOCALE): string {
  return `${locale.cc}-${locale.lang}`;
}

/** Languages written right to left (ISO 639-1): the document's `dir` follows the language, never the country. */
const RTL_LANGUAGES: ReadonlySet<string> = new Set(['ar', 'fa', 'he', 'ps', 'sd', 'ug', 'ur', 'yi']);

/** `rtl` for Arabic, Persian, Hebrew, Urdu, …; `ltr` otherwise. */
export function localeDir(locale: Pick<Locale, 'lang'> = DEFAULT_LOCALE): 'ltr' | 'rtl' {
  return RTL_LANGUAGES.has(locale.lang.toLowerCase()) ? 'rtl' : 'ltr';
}

/**
 * The `<html>` attributes of a locale's pages: `lang` always, `dir="rtl"` only
 * for a right-to-left language, so a left-to-right page has no `dir` attribute
 * (its markup is exactly what it was before RTL support).
 */
export function htmlAttributes(locale: Pick<Locale, 'lang' | 'hreflang'>): { lang: string; dir?: 'rtl' } {
  return localeDir(locale) === 'rtl' ? { lang: locale.hreflang, dir: 'rtl' } : { lang: locale.hreflang };
}
